/**
 * Pénzformázó hook a FE-n.
 *
 * Az aktív csoport `GroupLocale`-jét használja (AuthProvider-ből). A hook
 * két függvényt ad vissza:
 *  - `money(amount)` → "1 234 Ft" / "1 234,56 €" stb.
 *  - `num(amount, decimals?)` → currency nélküli számformázás
 *
 * Ha a locale még nem töltődött be (vagy nincs aktív csoport), a default
 * 'hu-HU' / 'HUF' formátum érvényesül — a kompon nem törik meg, csak a
 * megjelenés lesz a default-ra állítva.
 */

import { useMemo } from 'react';
import { useAuth } from '../auth/AuthProvider';
import {
  formatMoney,
  formatNumber,
  DEFAULT_NUMBER_FORMAT,
  type NumberFormat,
} from '@pta/i18n/format';

interface MoneyFormatter {
  /** Pénzformátum: szám + currency symbol. */
  money: (amount: string | number) => string;
  /** Csak szám, ezres elválasztóval. */
  num: (amount: string | number, decimals?: number) => string;
  /** Kényelmi: a táblázat-fej oszlop nevéhez használható currency-jelzés. */
  currencyLabel: string;
  /** A locale objektum részei (ha közvetlen kell). */
  numberFormat: NumberFormat;
  currencySymbol: string | null;
  currency: string;
  currencyMinorUnit: number;
}

export function useMoneyFormatter(): MoneyFormatter {
  const { activeGroupLocale } = useAuth();

  return useMemo(() => {
    const fmt = activeGroupLocale?.numberFormat ?? DEFAULT_NUMBER_FORMAT;
    const symbol = activeGroupLocale?.currencySymbol ?? null;
    const currency = activeGroupLocale?.currency ?? 'HUF';
    const minor = activeGroupLocale?.currencyMinorUnit ?? 0;

    return {
      money: (amount) => formatMoney(amount, fmt, symbol, minor),
      num: (amount, decimals) => formatNumber(amount, fmt, decimals),
      currencyLabel: symbol ?? currency,
      numberFormat: fmt,
      currencySymbol: symbol,
      currency,
      currencyMinorUnit: minor,
    };
  }, [activeGroupLocale]);
}
